import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, toast } from "@/App";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Bell, BellOff, Mail, MessageCircle, Activity, TrendingUp, BarChart3 } from "lucide-react";

const ALERT_TYPES = [
  { id: "price_move", label: "Price Move", icon: TrendingUp, desc: "Large price swings" },
  { id: "volume_spike", label: "Volume Spike", icon: BarChart3, desc: "Unusual volume" },
  { id: "oi_spike", label: "OI Spike", icon: Activity, desc: "Open interest changes" },
];

const SENSITIVITY_OPTIONS = [
  { value: "low", label: "Low", desc: "Only major moves" },
  { value: "medium", label: "Medium", desc: "Balanced" },
  { value: "high", label: "High", desc: "More frequent" },
];

export function SmartAlertToggle({ symbol, compact = false }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/smart-alerts/configs/${symbol}`);
      setConfig(resp.data?.config || null);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleQuickToggle = async (e) => {
    e.stopPropagation();
    if (!config) {
      // Create default config
      try {
        const resp = await axios.post(`${API}/smart-alerts/configs`, {
          symbol,
          enabled: true,
          alert_types: ["price_move"],
          sensitivity: "medium",
          channels: { email: false, whatsapp: false },
          market_hours_only: true,
        });
        setConfig(resp.data?.config);
        toast.success(`Smart alerts enabled for ${symbol}`);
      } catch {
        toast.error("Failed to enable alerts");
      }
    } else {
      // Toggle existing
      const newEnabled = !config.enabled;
      try {
        await axios.post(`${API}/smart-alerts/configs/${symbol}/toggle`, { enabled: newEnabled });
        setConfig(prev => ({ ...prev, enabled: newEnabled }));
        toast.success(newEnabled ? `Alerts on for ${symbol}` : `Alerts off for ${symbol}`);
      } catch {
        toast.error("Failed to toggle");
      }
    }
  };

  const openConfig = (e) => {
    e.stopPropagation();
    setDialogOpen(true);
  };

  if (loading) return null;

  const isActive = config?.enabled;

  if (compact) {
    return (
      <>
        <button
          onClick={isActive ? openConfig : handleQuickToggle}
          className={`p-1.5 rounded-lg transition-all ${
            isActive
              ? "text-amber-600 bg-amber-50 hover:bg-amber-100"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          }`}
          data-testid={`smart-alert-toggle-${symbol}`}
          title={isActive ? "Alert active — click to configure" : "Enable smart alerts"}
        >
          {isActive ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
        </button>
        <SmartAlertDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          symbol={symbol}
          config={config}
          onUpdate={setConfig}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant={isActive ? "default" : "outline"}
        size="sm"
        onClick={isActive ? openConfig : handleQuickToggle}
        className={isActive ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
        data-testid={`smart-alert-btn-${symbol}`}
      >
        {isActive ? <Bell className="w-3.5 h-3.5 mr-1.5" /> : <BellOff className="w-3.5 h-3.5 mr-1.5" />}
        {isActive ? "Alert On" : "Set Alert"}
      </Button>
      <SmartAlertDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        symbol={symbol}
        config={config}
        onUpdate={setConfig}
      />
    </>
  );
}

function SmartAlertDialog({ open, onOpenChange, symbol, config, onUpdate }) {
  const [alertTypes, setAlertTypes] = useState(config?.alert_types || ["price_move"]);
  const [sensitivity, setSensitivity] = useState(config?.sensitivity || "medium");
  const [emailEnabled, setEmailEnabled] = useState(config?.channels?.email || false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(config?.channels?.whatsapp || false);
  const [marketHoursOnly, setMarketHoursOnly] = useState(config?.market_hours_only ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setAlertTypes(config.alert_types || ["price_move"]);
      setSensitivity(config.sensitivity || "medium");
      setEmailEnabled(config.channels?.email || false);
      setWhatsappEnabled(config.channels?.whatsapp || false);
      setMarketHoursOnly(config.market_hours_only ?? true);
    }
  }, [config]);

  const toggleAlertType = (typeId) => {
    setAlertTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.length > 1 ? prev.filter(t => t !== typeId) : prev;
      }
      return [...prev, typeId];
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/smart-alerts/configs`, {
        symbol,
        enabled: true,
        alert_types: alertTypes,
        sensitivity,
        channels: { email: emailEnabled, whatsapp: whatsappEnabled },
        market_hours_only: marketHoursOnly,
      });
      onUpdate(resp.data?.config);
      toast.success(`Alert config saved for ${symbol}`);
      onOpenChange(false);
    } catch {
      toast.error("Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/smart-alerts/configs/${symbol}`);
      onUpdate(null);
      toast.success(`Alerts removed for ${symbol}`);
      onOpenChange(false);
    } catch {
      toast.error("Failed to remove config");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="smart-alert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Smart Alerts — {symbol}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Alert Types */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Alert Types</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {ALERT_TYPES.map(({ id, label, icon: Icon, desc }) => {
                const active = alertTypes.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleAlertType(id)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
                      active
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                    data-testid={`alert-type-${id}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{label}</span>
                    <span className="text-[10px] text-gray-400 leading-tight">{desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sensitivity */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sensitivity</label>
            <div className="flex gap-2 mt-2">
              {SENSITIVITY_OPTIONS.map(({ value, label, desc }) => (
                <button
                  key={value}
                  onClick={() => setSensitivity(value)}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 transition-all text-center ${
                    sensitivity === value
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                  data-testid={`sensitivity-${value}`}
                >
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-[10px] text-gray-400">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Delivery Channels */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery Channels</label>
            <div className="space-y-2 mt-2">
              <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-gray-600" />
                  <span className="text-sm">In-App</span>
                </div>
                <Badge variant="outline" className="text-[10px]">Always on</Badge>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-600" />
                  <span className="text-sm">Email</span>
                </div>
                <Switch
                  checked={emailEnabled}
                  onCheckedChange={setEmailEnabled}
                  data-testid="channel-email-toggle"
                />
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-gray-600" />
                  <span className="text-sm">WhatsApp</span>
                </div>
                <Switch
                  checked={whatsappEnabled}
                  onCheckedChange={setWhatsappEnabled}
                  data-testid="channel-whatsapp-toggle"
                />
              </div>
            </div>
          </div>

          {/* Market Hours */}
          <div className="flex items-center justify-between p-2 rounded-lg border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-700">Market hours only</p>
              <p className="text-[10px] text-gray-400">9:15 AM – 3:30 PM IST, Mon-Fri</p>
            </div>
            <Switch
              checked={marketHoursOnly}
              onCheckedChange={setMarketHoursOnly}
              data-testid="market-hours-toggle"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          {config && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={handleDelete}
              data-testid="smart-alert-delete-btn"
            >
              Remove Alert
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSave}
              disabled={saving}
              data-testid="smart-alert-save-btn"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SmartAlertToggle;
